import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useNegocio } from './AliadoDashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Settings, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function AliadoSettingsPage() {
    const negocio = useNegocio();
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!negocio) return;
        setForm({
            name: negocio.name || '',
            tagline: negocio.tagline || '',
            description: negocio.description || '',
            logo_url: negocio.logo_url || '',
            cover_url: negocio.cover_url || '',
            phone: negocio.phone || '',
            whatsapp: negocio.whatsapp || '',
            email: negocio.email || '',
            address: negocio.address || '',
            delivery_available: negocio.delivery_available || false,
            delivery_cost: negocio.delivery_cost || 0,
            min_order: negocio.min_order || 0,
        });
    }, [negocio]);

    const handleSave = async () => {
        if (!form.name.trim()) return toast.error('El nombre es requerido');
        setSaving(true);
        try {
            await api.put('/me/oknegocio', form);
            toast.success('Negocio actualizado. Recarga para ver los cambios.');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar');
        } finally { setSaving(false); }
    };

    if (!form) return <div className="text-sm text-muted-foreground py-8 text-center">Cargando…</div>;

    const field = (label, key, type = 'text', placeholder = '') => (
        <div>
            <label className="text-xs font-medium">{label}</label>
            <Input type={type} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
                className="mt-1" placeholder={placeholder} />
        </div>
    );

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-xl font-bold flex items-center gap-2"><Settings className="h-5 w-5 text-orange-600" /> Mi negocio</h1>
                <p className="text-sm text-muted-foreground">Edita la información pública de tu negocio.</p>
            </div>

            <div className="rounded-2xl border bg-card p-5 space-y-4">
                <h2 className="font-semibold text-sm border-b pb-2">Información básica</h2>
                {field('Nombre del negocio *', 'name', 'text', 'Nombre de tu negocio')}
                {field('Tagline', 'tagline', 'text', 'Frase corta que describe tu negocio')}
                <div>
                    <label className="text-xs font-medium">Descripción</label>
                    <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                        className="mt-1 text-sm" rows={3} placeholder="Cuéntale a tus clientes sobre tu negocio…" />
                </div>
                {field('URL de logo', 'logo_url', 'text', 'https://…')}
                {field('URL de portada', 'cover_url', 'text', 'https://…')}
            </div>

            <div className="rounded-2xl border bg-card p-5 space-y-4">
                <h2 className="font-semibold text-sm border-b pb-2">Contacto</h2>
                {field('Teléfono', 'phone', 'text', '55 1234 5678')}
                {field('WhatsApp', 'whatsapp', 'text', '5512345678')}
                {field('Email', 'email', 'email', 'correo@negocio.com')}
                {field('Dirección', 'address', 'text', 'Calle, número, colonia')}
            </div>

            <div className="rounded-2xl border bg-card p-5 space-y-4">
                <h2 className="font-semibold text-sm border-b pb-2">Entrega</h2>
                <div className="flex items-center gap-3">
                    <Switch id="deliv" checked={form.delivery_available} onCheckedChange={v => setForm(p => ({ ...p, delivery_available: v }))} />
                    <label htmlFor="deliv" className="text-sm">Ofrecer entrega a domicilio</label>
                </div>
                {form.delivery_available && (
                    <div className="grid grid-cols-2 gap-3">
                        {field('Costo de envío ($)', 'delivery_cost', 'number', '0')}
                        {field('Pedido mínimo ($)', 'min_order', 'number', '0')}
                    </div>
                )}
            </div>

            <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700 w-full sm:w-auto">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Guardando…' : 'Guardar cambios'}
            </Button>
        </div>
    );
}
